import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonPage,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import { decodeParam } from "../util/ApiHelper";
import PageHeader from "./PageHeader";
import ApiError from "./ApiError";
import {
  checkDuplicateBOL,
  getPurchaseOrderTermsData,
} from "../api/PurchaseOrders";
import { POReceiptData, PurchaseOrderDetails } from "../models/purchase-order";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import POLine from "./POLine";
import {
  createPOReceipts,
  CreatePOReceiptsRequestObject,
  getPoReceiptsData,
} from "../api/PurchaseOrderLines";
import { getConfirmation } from "../util/PoHelper";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import {
  barcodeOutline,
  barcodeSharp,
  filterCircleOutline,
  filterCircleSharp,
} from "ionicons/icons";
import { useBarcodeManager } from "../hooks/useBarcodeManager";

type Params = {
  poNumber: string;
};

const POReceiptsDetails: React.FC = () => {
  const { poNumber } = useParams<Params>();
  const location = useLocation();

  // Parse query parameters from the URL
  const queryParams = new URLSearchParams(location.search);

  const billOfLading = queryParams.get("billOfLanding") ?? "";
  const comments = queryParams.get("comments") ?? "";
  const employee = queryParams.get("employee") ?? "";
  const displayPONumber = decodeParam(poNumber);
  const [showLoading, setShowLoading] = useState(false);
  const [errorData, setErrorData] = useState("");
  const [poDetails, setPODetails] = useState<PurchaseOrderDetails>();
  const [poDetailsData, setPODetailsData] = useState<POReceiptData[]>();
  const [filteredPODetails, setFilteredPODetails] = useState<POReceiptData[]>();
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [isDuplicateBOL, setIsDuplicateBOL] = useState(false);
  const [updatedPO, setUpdatedPO] = useState<CreatePOReceiptsRequestObject[]>(
    []
  );
  const { translations } = useContext(TranslationsContext);
  const [searchValue, setSearchValue] = useState("");
  const [presentToast] = useIonToast();

  const handlePOBarCodeScan = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const { scanClick } = useBarcodeManager(handlePOBarCodeScan);

  useEffect(() => {
    //handle search
    if (!searchValue) {
      setFilteredPODetails(poDetailsData);
    } else {
      const filteredData = poDetailsData?.filter(
        (p) => p.Item.toLowerCase().includes(searchValue.toLowerCase()) //case insensitive
      );
      setFilteredPODetails(filteredData);
    }
  }, [poDetailsData, searchValue]);

  const checkForDuplicateBOL = useCallback(
    async (supplier: string) => {
      if (!billOfLading) return;
      //checkDuplicateBOL
      const duplicateBOLResponse = await checkDuplicateBOL({
        billOfLading,
        comments: comments ?? "",
        supplier,
      });
      if (duplicateBOLResponse.isError) {
        setErrorData(duplicateBOLResponse.data?.toString());
      } else {
        setIsDuplicateBOL(duplicateBOLResponse.data);
      }
    },
    [billOfLading, comments]
  );

  const getPODataForPONumber = useCallback(async () => {
    const poReceiptsDataResponse = await getPoReceiptsData({
      poNumber: displayPONumber,
    });
    if (poReceiptsDataResponse.isError) {
      setErrorData(poReceiptsDataResponse.data?.toString());
    } else {
      setPODetailsData(poReceiptsDataResponse.data);
      setFilteredPODetails(poReceiptsDataResponse.data);
    }
  }, [displayPONumber]);

  useEffect(() => {
    //get the PO details
    (async () => {
      setShowLoading(true);
      const response = await getPurchaseOrderTermsData({
        poNumber: poNumber,
      });
      if (response.isError) {
        setErrorData(response.data?.toString());
      } else {
        const purchaseOrderDetails = response.data as PurchaseOrderDetails;
        setPODetails(purchaseOrderDetails);
        if (purchaseOrderDetails.SupplierData) {
          await checkForDuplicateBOL(
            purchaseOrderDetails.SupplierData.Supplier
          );
          await getPODataForPONumber();
        }
      }
      setShowLoading(false);
    })();
  }, [checkForDuplicateBOL, getPODataForPONumber, poNumber]);

  const handleUpdatePO = useCallback(
    (poData: CreatePOReceiptsRequestObject) => {
      setUpdatedPO((pre) => {
        const po = pre.find(
          (i) => i.Item === poData.Item && i.Bin === poData.Bin
        );
        if (po) {
          //update
          return pre.map((p) => {
            if (p.Item === poData.Item && p.Bin === poData.Bin) return poData;
            return p;
          });
        }
        //add
        return [...pre, poData];
      });
    },
    []
  );

  const updatePODetails = useCallback((data: POReceiptData[]) => {
    setPODetailsData((pre) =>
      (pre ?? []).map((p) => {
        const updateData = data.find((d) => d.Item === p.Item);
        if (updateData) return { ...p, Result: updateData.Result };
        return { ...p, Result: undefined };
      })
    );
  }, []);

  const updateFilteredPODetails = useCallback((data: POReceiptData[]) => {
    setFilteredPODetails((pre) =>
      (pre ?? []).map((p) => {
        const updateData = data.find((d) => d.Item === p.Item);
        if (updateData) return { ...p, Result: updateData.Result };
        return { ...p, Result: undefined };
      })
    );
  }, []);

  const handleSave = useCallback(async () => {
    setShowLoading(true);
    //save quantity received for this POLine
    const response = await createPOReceipts({
      Confirmation: getConfirmation(),
      RequestObject: updatedPO,
    });

    if (response.isError) {
      setErrorData(response.data?.toString());
    } else {
      const data: POReceiptData[] = response.data;
      const receivedSuccessfully = data.every((d) => !!d.Result?.Success);
      if (receivedSuccessfully)
        presentToast({
          message: `Quantity received successfully for Line(s) ${updatedPO
            .map((p) => p.PoLine)
            .join(", ")} `,
          duration: 3000,
          position: "top",
          color: "success",
        });
      //update the PO Data result
      updatePODetails(data);
      updateFilteredPODetails(data);
      setUpdatedPO([]);
    }
    setShowLoading(false);
  }, [presentToast, updateFilteredPODetails, updatePODetails, updatedPO]);

  const hasPOs = !!filteredPODetails?.length;
  const isPOClosed = poDetails?.Status === "Closed";

  return (
    <IonPage>
      <IonLoading isOpen={showLoading} />
      <PageHeader title={displayPONumber} showBack />
      <ApiError errorData={errorData} />
      <IonToolbar>
        <IonItem hidden={!poDetailsData?.length}>
          <IonInput
            type="text"
            clearInput={true}
            debounce={500}
            class="ion-text-begin"
            placeholder={translations["lbl_filter"] || "Filter"}
            onIonChange={(ev) => {
              setSearchValue(String(ev.detail.value));
            }}
            value={searchValue}
          ></IonInput>
          <IonIcon
            slot="end"
            ios={filterCircleOutline}
            md={filterCircleSharp}
          ></IonIcon>
          <IonButton slot="end" onClick={scanClick} fill="clear" size="small">
            <IonIcon
              ios={barcodeOutline}
              md={barcodeSharp}
              slot="icon-only"
            ></IonIcon>
          </IonButton>
        </IonItem>
      </IonToolbar>
      <IonContent className="ion-padding">
        <div style={{ padding: 8 }} hidden={!isPOClosed || showLoading}>
          <IonLabel color={"danger"}>
            {translatedMessages["MobPOClosed"]?.MessageText ||
              "Purchase Order is closed. You must re-open the purchase order first."}
          </IonLabel>
        </div>
        <div
          style={{ padding: 8 }}
          hidden={showLoading || hasPOs || isPOClosed || !!errorData}
        >
          <IonLabel color={"warning"}>
            {translatedMessages["NoResultsFound"]?.MessageText ||
              "No results found."}
          </IonLabel>
        </div>
        <div style={{ padding: 8 }} hidden={showLoading || !isDuplicateBOL}>
          <IonLabel color={"warning"}>
            {translatedMessages["MobPOLandingNumberAlreadyUsed"]?.MessageText ||
              `This Bill Of Lading Number has already been used by this supplier.
            If you choose to use this Bill Of Lading Number, you will be
            required to enter in comments before proceeding to the receipts
            window.`}
          </IonLabel>
        </div>
        {hasPOs &&
          !isDuplicateBOL &&
          !isPOClosed &&
          (filteredPODetails ?? []).map((entry) => {
            return (
              <POLine
                key={entry.ObjectId}
                line={entry}
                employee={employee}
                billOfLading={billOfLading}
                onPoUpdate={handleUpdatePO}
                hasPendingUpdates={updatedPO.some(
                  (u) => u.PoLine === entry.Line
                )}
              />
            );
          })}
      </IonContent>
      <IonFooter translucent={true} hidden={updatedPO.length === 0}>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} fill="solid">
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  POReceiptsDetails,
  "POReceiptsDetails"
);
